"use client"

import { Check } from "lucide-react"
import { indexVar, twoDigit } from "@/components/marketing/landing-v2/cssVars"
import { Mascot } from "@/components/marketing/landing-v2/Mascot"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import { AGENT_ACTION_IDS, AGENT_SOURCE_IDS } from "@/lib/landing-v2/content"
import type { Landing2Copy } from "@/lib/landing-v2/copy"

function Bus({ direction }: { direction: "in" | "out" }) {
  return (
    <div className="l2-bus" data-direction={direction} aria-hidden="true">
      <i className="l2-bus-line" />
      {AGENT_SOURCE_IDS.map((id, index) => (
        <span className="l2-bus-lane" key={id} style={indexVar(index)}>
          <i className="l2-bus-tick" />
          <i className="l2-bus-packet" />
        </span>
      ))}
    </div>
  )
}

/**
 * Signals arrive from the tools a business already runs, the agent decides, and
 * work leaves the other side already done.
 */
export function AgentNetwork({ copy }: { copy: Landing2Copy }) {
  const copyRef = useReveal<HTMLDivElement>()
  const networkRef = useReveal<HTMLDivElement>()

  return (
    <section className="l2-section" id="agents">
      <div className="l2-container l2-agent-grid">
        <div className="l2-agent-copy l2-stagger" ref={copyRef}>
          <span className="l2-label">{copy.agent.label}</span>
          <h2 className="l2-h2">{copy.agent.title}</h2>
          <p className="l2-body">{copy.agent.body}</p>
          <p className="l2-agent-emphasis">{copy.agent.emphasis}</p>
          <ol className="l2-phase-list">
            {copy.agent.phases.map((phase, index) => (
              <li key={phase} style={indexVar(index)}>
                <span className="l2-phase-index">{twoDigit(index + 1)}</span>
                {phase}
              </li>
            ))}
          </ol>
        </div>

        <div className="l2-network" ref={networkRef}>
          <span className="l2-label l2-network-label">{copy.agent.sourcesLabel}</span>
          <div className="l2-network-row">
            {AGENT_SOURCE_IDS.map((id, index) => (
              <span className="l2-network-chip" key={id} style={indexVar(index)}>
                {copy.agent.sources[id]}
              </span>
            ))}
          </div>

          <Bus direction="in" />

          <div className="l2-network-core">
            <Mascot size={46} />
            <span className="l2-network-core-label">{copy.agent.coreLabel}</span>
            <span className="l2-network-phase" aria-hidden="true">
              {copy.agent.phases.map((phase, index) => (
                <b key={phase} style={indexVar(index)}>
                  {phase}
                </b>
              ))}
            </span>
          </div>

          <Bus direction="out" />

          <span className="l2-label l2-network-label">{copy.agent.actionsLabel}</span>
          <div className="l2-network-row">
            {AGENT_ACTION_IDS.map((id, index) => (
              <span className="l2-network-chip" data-done="true" key={id} style={indexVar(index)}>
                <Check size={11} strokeWidth={3} />
                {copy.agent.actions[id]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
